import { PLATFORM_LABELS, platformFamilyOf, uaDataPlatformFamily } from '@fraudeai/schema';
import { defineRule, type Rule } from '../types.js';

/**
 * Внутренняя непротиворечивость окружения.
 *
 * Здесь проверяется не «правда ли это Windows», а «сходится ли история».
 * Браузер сообщает о себе через десяток независимых каналов: UA-строка,
 * Client Hints, `navigator.platform`, набор шрифтов, голоса синтеза речи,
 * поведение движка JS. Подделать один канал легко, все сразу — почти
 * невозможно, потому что часть из них приходит прямо из операционной системы.
 */

const ENGINE_LABELS: Record<string, string> = {
  v8: 'V8 (Chrome, Edge, Opera)',
  spidermonkey: 'SpiderMonkey (Firefox)',
  javascriptcore: 'JavaScriptCore (Safari)',
};

/** Приводит вывод по голосам/шрифтам к семейству платформы. */
function normalizeOsGuess(guess: string | null): string[] | null {
  if (!guess) return null;
  switch (guess) {
    case 'windows':
      return ['windows'];
    case 'macos':
      return ['macos'];
    case 'macos-or-ios':
      return ['macos', 'ios'];
    case 'linux':
      return ['linux'];
    case 'android-or-chromeos':
      return ['android', 'linux'];
    case 'android':
      return ['android'];
    default:
      return null;
  }
}

/**
 * Схлопывает платформы, неразличимые по системным маркерам.
 *
 * iOS и macOS несут один набор шрифтов и одни голоса синтеза речи — это одна
 * экосистема Apple, и различить их по этим признакам нельзя в принципе.
 * Android и Linux делят часть шрифтов по той же причине. Требовать от маркера
 * различения внутри такой пары значит гарантированно ошибаться на настоящих
 * устройствах: поймано корпусом профилей на Safari для iPhone.
 */
function markerFamily(platform: string): string {
  if (platform === 'macos' || platform === 'ios') return 'apple';
  if (platform === 'android' || platform === 'linux') return 'linux-based';
  return platform;
}

function markerMatches(guessed: string[], actual: string): boolean {
  const target = markerFamily(actual);
  return guessed.some((family) => markerFamily(family) === target);
}

/**
 * Значения navigator.platform, допустимые для каждого семейства из UA.
 *
 * Точного совпадения требовать нельзя: на Android свойство штатно возвращает
 * «Linux armv8l» — там внутри действительно ядро Linux. Строгое сравнение
 * давало срабатывание у каждого пользователя Android. iPad в режиме «как на
 * компьютере» точно так же представляется macOS.
 */
const PLATFORM_COMPATIBLE: Record<string, string[]> = {
  windows: ['windows'],
  macos: ['macos'],
  linux: ['linux'],
  android: ['android', 'linux'],
  ios: ['ios', 'macos'],
};

function platformMatches(uaFamily: string, platformFamily: string): boolean {
  const allowed = PLATFORM_COMPATIBLE[uaFamily];
  return !allowed || allowed.includes(platformFamily);
}

function labelOf(family: string): string {
  return PLATFORM_LABELS[family] ?? family;
}

export const consistencyRules: Rule[] = [
  defineRule({
    meta: {
      id: 'cons.platform-vs-ua',
      title: 'navigator.platform не совпадает с User-Agent',
      severity: 'critical',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 40,
      evidenceTier: 'corroborating',
      evidenceGroup: 'identity-surface',
      requires: ['navigator'],
    },
    evaluate(signals) {
      const nav = signals.navigator;
      const parsed = nav?.parsed;
      if (!nav || !parsed) return null;

      const fromPlatform = platformFamilyOf(nav.platform);
      if (!fromPlatform || platformMatches(parsed.platformFamily, fromPlatform)) return null;

      return {
        explanation:
          `User-Agent описывает ${labelOf(parsed.platformFamily)}, а свойство ` +
          `navigator.platform — ${labelOf(fromPlatform)}. Оба значения задаёт браузер, ` +
          'и в норме они всегда согласованы.',
        evidence: [
          { label: 'User-Agent', actual: nav.userAgent ?? '—' },
          { label: 'navigator.platform', expected: labelOf(parsed.platformFamily), actual: nav.platform ?? '—' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.uadata-vs-ua',
      title: 'Client Hints не совпадают с User-Agent',
      severity: 'critical',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 40,
      evidenceTier: 'corroborating',
      evidenceGroup: 'client-hints',
      requires: ['navigator'],
    },
    evaluate(signals) {
      const nav = signals.navigator;
      const parsed = nav?.parsed;
      if (!nav?.uaData || !parsed) return null;

      const findings: string[] = [];
      const evidence = [];

      const fromHints = uaDataPlatformFamily(nav.uaData.platform);
      if (fromHints && fromHints !== parsed.platformFamily) {
        findings.push('платформа');
        evidence.push({
          label: 'Платформа',
          expected: labelOf(parsed.platformFamily),
          actual: `${nav.uaData.platform} (${labelOf(fromHints)})`,
        });
      }

      if (nav.uaData.mobile !== null && nav.uaData.mobile !== parsed.isMobile) {
        findings.push('признак мобильного устройства');
        evidence.push({
          label: 'Мобильное устройство',
          expected: String(parsed.isMobile),
          actual: String(nav.uaData.mobile),
        });
      }

      // Client Hints отдают полную версию браузера — старший разряд обязан
      // совпадать с версией из UA-строки.
      const hintedVersion = nav.uaData.uaFullVersion ?? null;
      if (hintedVersion && parsed.browserMajor !== null) {
        const hintedMajor = Number.parseInt(hintedVersion.split('.')[0] ?? '', 10);
        if (Number.isFinite(hintedMajor) && hintedMajor !== parsed.browserMajor) {
          findings.push('версия браузера');
          evidence.push({
            label: 'Версия браузера',
            expected: String(parsed.browserMajor),
            actual: hintedVersion,
          });
        }
      }

      if (findings.length === 0) return null;

      return {
        explanation:
          `Client Hints и User-Agent расходятся: ${findings.join(', ')}. Это два способа ` +
          'сообщить одно и то же, и браузер заполняет их из одного источника. ' +
          'Расхождение означает, что подменён только один из них.',
        evidence,
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.engine-vs-ua',
      title: 'Движок JavaScript не соответствует заявленному браузеру',
      severity: 'critical',
      category: 'spoofing',
      flags: ['SPOOFING', 'ANTIDETECT'],
      weight: 45,
      evidenceTier: 'direct',
      evidenceGroup: 'engine-behavior',
      requires: ['engine', 'navigator'],
    },
    evaluate(signals) {
      const engine = signals.engine;
      const parsed = signals.navigator?.parsed;
      if (!engine || !parsed || !engine.guessFromUa) return null;
      if (engine.guess === 'unknown' || engine.guess === engine.guessFromUa) return null;

      return {
        explanation:
          `User-Agent сообщает о браузере ${parsed.browser}, которому соответствует движок ` +
          `${ENGINE_LABELS[engine.guessFromUa] ?? engine.guessFromUa}. Но измеренное поведение ` +
          `движка — ${ENGINE_LABELS[engine.guess] ?? engine.guess}. Поведение движка задаётся ` +
          'самим браузером и подмене не поддаётся, а вот UA-строка меняется одной настройкой.',
        evidence: [
          { label: 'Заявленный браузер', actual: `${parsed.browser} ${parsed.browserVersion}` },
          {
            label: 'Движок JS',
            expected: ENGINE_LABELS[engine.guessFromUa] ?? engine.guessFromUa,
            actual: ENGINE_LABELS[engine.guess] ?? engine.guess,
          },
          { label: 'Форма стек-трейса', actual: engine.stackShape ?? '—' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.feature-age',
      title: 'Набор возможностей не соответствует заявленной версии браузера',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 32,
      evidenceTier: 'corroborating',
      evidenceGroup: 'engine-age',
      requires: ['engine', 'navigator'],
    },
    evaluate(signals) {
      const engine = signals.engine;
      const parsed = signals.navigator?.parsed;
      // Сверка идёт с версией Chromium, а не с версией браузера: у Yandex,
      // Opera и Vivaldi собственная нумерация, и сравнение с ней давало
      // ложное срабатывание на любом таком браузере.
      if (!engine || !parsed?.chromiumMajor) return null;

      const { featureAgeMin, featureAgeMax, missingExpectedFeatures, unexpectedFeatures } = engine;
      if (featureAgeMin === null && featureAgeMax === null) return null;

      const claimed = parsed.chromiumMajor;
      const tooOld = featureAgeMin !== null && claimed < featureAgeMin;
      const tooNew = featureAgeMax !== null && claimed > featureAgeMax;
      if (!tooOld && !tooNew) return null;

      const range =
        featureAgeMin !== null && featureAgeMax !== null
          ? `${featureAgeMin}–${featureAgeMax}`
          : featureAgeMin !== null
            ? `${featureAgeMin}+`
            : `до ${featureAgeMax}`;

      return {
        explanation:
          `Браузер заявляет Chromium версии ${claimed}, но по набору поддерживаемых ` +
          `возможностей это сборка версии ${range}. Каждая функция языка появляется ` +
          'в конкретном релизе — по их наличию и отсутствию версия вычисляется ' +
          'независимо от UA-строки.',
        evidence: [
          { label: 'Заявленная версия Chromium', actual: String(claimed) },
          { label: 'Версия по возможностям', expected: range },
          ...(unexpectedFeatures?.length
            ? [{ label: 'Есть, хотя не должно быть', detail: unexpectedFeatures.slice(0, 5).join('; ') }]
            : []),
          ...(missingExpectedFeatures?.length
            ? [{ label: 'Нет, хотя должно быть', detail: missingExpectedFeatures.slice(0, 5).join('; ') }]
            : []),
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.feature-age-impossible',
      title: 'Набор возможностей не соответствует ни одной реальной сборке',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING', 'ANTIDETECT'],
      weight: 35,
      evidenceTier: 'corroborating',
      evidenceGroup: 'engine-age',
      requires: ['engine'],
    },
    evaluate(signals) {
      const engine = signals.engine;
      if (engine?.featureAgeMin == null || engine.featureAgeMax == null) return null;
      if (engine.featureAgeMin <= engine.featureAgeMax) return null;

      return {
        explanation:
          'Часть современных возможностей присутствует, а часть более старых — отсутствует. ' +
          'Такого сочетания нет ни в одной реальной версии браузера: возможности только ' +
          'добавляются. Похоже, часть API скрыта или подделана намеренно.',
        evidence: [
          { label: 'Минимальная версия по наличию', actual: String(engine.featureAgeMin) },
          { label: 'Максимальная версия по отсутствию', actual: String(engine.featureAgeMax) },
          { label: 'Нет, хотя должно быть', detail: engine.missingExpectedFeatures?.join('; ') ?? '—' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.voices-vs-ua',
      title: 'Голоса синтеза речи не от заявленной системы',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'os-marker:voices',
      requires: ['os', 'navigator'],
    },
    evaluate(signals) {
      const guess = normalizeOsGuess(signals.os?.voicesOsGuess ?? null);
      const parsed = signals.navigator?.parsed;
      if (!guess || !parsed) return null;
      if (markerMatches(guess, parsed.platformFamily)) return null;

      const sample = (signals.os?.voices ?? [])
        .filter((v) => v.localService)
        .slice(0, 6)
        .map((v) => v.name);

      return {
        explanation:
          `Голоса синтеза речи установлены операционной системой, и здесь они принадлежат ` +
          `${guess.map(labelOf).join(' или ')}, тогда как браузер заявляет ` +
          `${labelOf(parsed.platformFamily)}. Список голосов приходит из системы, ` +
          'а не из браузера, поэтому подделывают его редко.',
        evidence: [
          { label: 'Заявленная система', actual: labelOf(parsed.platformFamily) },
          { label: 'Система по голосам', actual: guess.map(labelOf).join(' / ') },
          { label: 'Системные голоса', detail: sample.join(', ') || '—' },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.fonts-vs-ua',
      title: 'Набор шрифтов не от заявленной системы',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 28,
      evidenceTier: 'corroborating',
      evidenceGroup: 'os-marker:fonts',
      requires: ['fonts', 'navigator'],
    },
    evaluate(signals) {
      const guess = normalizeOsGuess(signals.fonts?.osGuess ?? null);
      const parsed = signals.navigator?.parsed;
      if (!guess || !parsed) return null;
      if (markerMatches(guess, parsed.platformFamily)) return null;

      return {
        explanation:
          `Установленные шрифты характерны для ${guess.map(labelOf).join(' или ')}, ` +
          `а браузер сообщает о ${labelOf(parsed.platformFamily)}. Шрифты ставит система ` +
          'вместе с собой — подменить их согласованно сложнее, чем UA-строку.',
        evidence: [
          { label: 'Заявленная система', actual: labelOf(parsed.platformFamily) },
          { label: 'Система по шрифтам', actual: guess.map(labelOf).join(' / ') },
          { label: 'Найдено шрифтов', actual: String(signals.fonts?.detectedCount ?? 0) },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.gecko-only-properties',
      title: 'Свойства Firefox в браузере, который называет себя иначе',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'engine-surface',
      requires: ['navigator', 'engine'],
    },
    evaluate(signals) {
      const nav = signals.navigator;
      const engine = signals.engine;
      if (!nav || !engine) return null;

      const geckoOnly: string[] = [];
      if (nav.oscpu !== null) geckoOnly.push('navigator.oscpu');
      if (nav.buildID !== null) geckoOnly.push('navigator.buildID');
      if (geckoOnly.length === 0) return null;
      if (engine.guess === 'spidermonkey') return null;

      return {
        explanation:
          `В окружении присутствуют свойства ${geckoOnly.join(' и ')}, которые есть только ` +
          'в Firefox. При этом измеренный движок JavaScript — не SpiderMonkey. ' +
          'Похоже на попытку выдать один браузер за другой.',
        evidence: [
          { label: 'Свойства Firefox', detail: geckoOnly.join(', ') },
          { label: 'Измеренный движок', actual: ENGINE_LABELS[engine.guess] ?? engine.guess },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.timezone-inconsistent',
      title: 'Часовой пояс и системное время не согласуются',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 30,
      evidenceTier: 'corroborating',
      evidenceGroup: 'timezone-consistency',
      requires: ['locale'],
    },
    evaluate(signals) {
      const locale = signals.locale;
      if (locale?.dstConsistent !== false) return null;

      return {
        explanation:
          `Браузер сообщает часовой пояс ${locale.timezone}, но смещение времени, полученное ` +
          'через объект Date, ему не соответствует. Эти два источника читают одну и ту же ' +
          'настройку системы — расхождение означает, что подменён только один из них.',
        evidence: [
          { label: 'Часовой пояс (Intl)', actual: locale.timezone ?? '—' },
          { label: 'Смещение сейчас (Date)', actual: `${-(locale.timezoneOffsetNow ?? 0) / 60} ч от UTC` },
          {
            label: 'Смещение зимой / летом',
            actual: `${-(locale.timezoneOffsetJan ?? 0) / 60} ч / ${-(locale.timezoneOffsetJul ?? 0) / 60} ч`,
          },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.screen-impossible',
      title: 'Невозможные параметры экрана',
      severity: 'high',
      category: 'spoofing',
      flags: ['SPOOFING'],
      weight: 28,
      evidenceTier: 'corroborating',
      evidenceGroup: 'screen-validity',
      requires: ['screen'],
    },
    evaluate(signals) {
      const screen = signals.screen;
      if (!screen) return null;

      const problems: string[] = [];
      const evidence = [];

      if (
        screen.availWidth !== null &&
        screen.width !== null &&
        screen.availWidth > screen.width
      ) {
        problems.push('доступная ширина больше физической');
        evidence.push({ label: 'Ширина', expected: `≤ ${screen.width}`, actual: String(screen.availWidth) });
      }
      if (
        screen.availHeight !== null &&
        screen.height !== null &&
        screen.availHeight > screen.height
      ) {
        problems.push('доступная высота больше физической');
        evidence.push({ label: 'Высота', expected: `≤ ${screen.height}`, actual: String(screen.availHeight) });
      }
      if (
        screen.innerWidth !== null &&
        screen.width !== null &&
        screen.innerWidth > screen.width + 32
      ) {
        problems.push('окно шире экрана');
        evidence.push({ label: 'Вьюпорт', expected: `≤ ${screen.width}`, actual: String(screen.innerWidth) });
      }

      if (problems.length === 0) return null;

      return {
        explanation:
          `Размеры экрана противоречат друг другу: ${problems.join('; ')}. Обычно так ` +
          'выглядит частичная подмена разрешения, когда изменили одни поля и забыли другие.',
        evidence,
      };
    },
  }),

  defineRule({
    meta: {
      id: 'cons.touch-vs-mobile',
      title: 'Сенсорный ввод не соответствует типу устройства',
      severity: 'medium',
      category: 'inconsistency',
      flags: ['SPOOFING', 'INCONSISTENT'],
      weight: 18,
      evidenceTier: 'corroborating',
      evidenceGroup: 'device-shape',
      requires: ['navigator', 'os'],
    },
    evaluate(signals) {
      const parsed = signals.navigator?.parsed;
      const touch = signals.os?.touchSupport;
      if (!parsed || !touch) return null;

      const points = touch.maxTouchPoints ?? 0;
      if (parsed.isMobile && points === 0 && !touch.touchStart) {
        return {
          explanation:
            'Браузер представляется мобильным, но устройство не поддерживает касания. ' +
            'У настоящего телефона или планшета сенсорный ввод есть всегда.',
          evidence: [
            { label: 'User-Agent', actual: 'мобильное устройство' },
            { label: 'maxTouchPoints', expected: '> 0', actual: String(points) },
          ],
        };
      }

      if (!parsed.isMobile && points > 0 && parsed.platformFamily === 'macos') {
        return {
          confidence: 0.6,
          explanation:
            'Заявлен macOS, но система сообщает о поддержке касаний. Сенсорных экранов ' +
            'у компьютеров Apple нет.',
          evidence: [{ label: 'maxTouchPoints', expected: '0', actual: String(points) }],
        };
      }

      return null;
    },
  }),

  defineRule({
    meta: {
      id: 'cons.desktop-motion-sensors',
      title: 'Десктопный профиль получает события датчиков движения',
      severity: 'info',
      category: 'hardware',
      flags: [],
      weight: 0,
      requires: ['navigator', 'os'],
    },
    evaluate(signals) {
      const parsed = signals.navigator?.parsed;
      const sensors = signals.os?.sensors;
      if (!parsed || !sensors) return null;
      if (parsed.isMobile || parsed.platformFamily === 'android' || parsed.platformFamily === 'ios') {
        return null;
      }
      if ((signals.navigator?.maxTouchPoints ?? 0) > 0) return null;
      if (!sensors.motionFired && !sensors.orientationFired) return null;

      const fired = [
        sensors.motionFired ? 'devicemotion' : null,
        sensors.orientationFired ? 'deviceorientation' : null,
      ].filter((v): v is string => Boolean(v));

      return {
        confidence: 0.35,
        explanation:
          'Профиль заявляет настольную систему без touch-экрана, но страница получила реальные ' +
          'события датчиков движения. На живых macOS/Chromium такие события тоже встречаются, ' +
          'поэтому это оставлено как справочное наблюдение без штрафа и без ярлыка антидетекта.',
        evidence: [
          { label: 'Заявленная система', actual: labelOf(parsed.platformFamily) },
          { label: 'maxTouchPoints', actual: String(signals.navigator?.maxTouchPoints ?? 0) },
          { label: 'Сработали события', detail: fired.join(', ') },
        ],
      };
    },
  }),
];
